import { useEffect, useRef } from "react";
import { useIsNewNativeApp } from "hooks/useIsApp";
import { sendWebViewEvent } from "@Utils/webviewEvents";
import { WEB_VIEW_APP_EVENTS } from "@Utils/webApputils/analyticEvents";

/**
 * Signals the native shell that the PDP above-the-fold content has painted.
 * The shell uses this to dismiss its loading skeleton early (before the full page finishes).
 *
 * Fires once per property; re-fires when navigating to a different property (SPA).
 * No-op outside a native WebView.
 *
 * NOTE: Android does not yet handle `PAGE_INTERACTIVE` (absent from `WebToNativeEvent`), so the
 * event is currently a no-op there — harmless, but its skeleton will not dismiss early until
 * Android adds the handler.
 */
export const usePageInteractiveSignal = (canonicalName?: string): void => {
  const isNewNativeApp = useIsNewNativeApp();
  const lastSignaled = useRef<string | null>(null);

  useEffect(() => {
    if (!isNewNativeApp) return undefined;
    if (!canonicalName) return undefined;
    if (lastSignaled.current === canonicalName) return undefined;

    // Double rAF ensures we fire after the browser has actually painted the above-the-fold.
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        sendWebViewEvent(WEB_VIEW_APP_EVENTS.PAGE_INTERACTIVE, { canonicalName });
        lastSignaled.current = canonicalName;
      });
    });

    return () => {
      cancelAnimationFrame(outer);
      if (inner) cancelAnimationFrame(inner);
    };
  }, [isNewNativeApp, canonicalName]);
};

export default usePageInteractiveSignal;
